import { useStorage } from '@vueuse/core'
import { computed, ref } from 'vue'

import type { HeadingDTO, SemesterListItem } from '@/types'

import { getMetadata } from '@/services/metadataService'

// ============================================
// Global State (shared across all components)
// ============================================

// Semesters state
const selectedSemester = useStorage<SemesterListItem | null>(
  'selectedSemester',
  null,
  sessionStorage,
  {
    serializer: {
      read: (value: string) => {
        try {
          return value ? JSON.parse(value) : null
        } catch {
          return null
        }
      },
      write: (value: SemesterListItem | null) => JSON.stringify(value),
    },
  },
)
const availableSemesters = ref<SemesterListItem[]>([])

// Headings state
const headings = ref<HeadingDTO[]>([])

// Shared state
const isLoading = ref(false)
const isInitialized = ref(false)
const error = ref<string | null>(null)

/**
 * Composable for managing global application metadata.
 * Fetches semesters and headings in a single API call for efficiency.
 */
export function useMetadata() {
  // ============================================
  // Core Functions
  // ============================================

  /**
   * Initialize all metadata (semesters + headings) in a single API call
   */
  async function initializeMetadata() {
    // Skip if already initialized
    if (isInitialized.value) {
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await getMetadata()

      // Process semesters
      availableSemesters.value = response.semesters.semesters
      processSemesterSelection(response.semesters)

      // Process headings
      headings.value = response.headings

      isInitialized.value = true
    } catch (err) {
      console.error('Error loading metadata:', err)
      error.value = err instanceof Error ? err.message : 'Failed to load metadata'
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Force refresh all metadata (bypasses initialization check)
   */
  async function refreshMetadata() {
    isLoading.value = true
    error.value = null

    try {
      const response = await getMetadata()

      availableSemesters.value = response.semesters.semesters
      processSemesterSelection(response.semesters)
      headings.value = response.headings

      isInitialized.value = true
    } catch (err) {
      console.error('Error refreshing metadata:', err)
      error.value = err instanceof Error ? err.message : 'Failed to refresh metadata'
    } finally {
      isLoading.value = false
    }
  }

  // ============================================
  // Semester Functions
  // ============================================

  function processSemesterSelection(semestersData: {
    semesters: SemesterListItem[]
    defaultSemesterId?: number
    activeSemesterId?: number
  }) {
    // Check if the stored semester is still valid
    if (selectedSemester.value) {
      const storedSemesterStillExists = semestersData.semesters.some(
        (s) => s.id === selectedSemester.value!.id,
      )

      if (storedSemesterStillExists) {
        // Update the stored semester with fresh data from the API
        const updatedSemester = semestersData.semesters.find(
          (s) => s.id === selectedSemester.value!.id,
        )
        if (updatedSemester) {
          selectedSemester.value = updatedSemester
        }
      } else {
        // Stored semester no longer exists, clear it
        selectedSemester.value = null
      }
    }

    // If no semester is selected, set the default one
    if (!selectedSemester.value && semestersData.semesters.length > 0) {
      setDefaultSemester(semestersData)
    }
  }

  function setDefaultSemester(data: {
    semesters: SemesterListItem[]
    defaultSemesterId?: number
    activeSemesterId?: number
  }) {
    // Try to find default semester first
    const defaultSemester = data.semesters.find((s) => s.isDefault)
    if (defaultSemester) {
      selectedSemester.value = defaultSemester
      return
    }

    // Fallback to active semester
    const activeSemester = data.semesters.find((s) => s.isCurrentlyActive)
    if (activeSemester) {
      selectedSemester.value = activeSemester
      return
    }

    // Final fallback to first semester
    if (data.semesters.length > 0) {
      selectedSemester.value = data.semesters[0]
    }
  }

  function selectSemester(semester: SemesterListItem) {
    selectedSemester.value = semester
  }

  function clearSelectedSemester() {
    selectedSemester.value = null
  }

  function getSemesterById(id: number): SemesterListItem | undefined {
    return availableSemesters.value.find((semester) => semester.id === id)
  }

  // ============================================
  // Heading Functions
  // ============================================

  /**
   * Recursively find a heading by ID in the heading tree
   */
  function getHeadingById(id: number): HeadingDTO | undefined {
    function findInList(list: HeadingDTO[]): HeadingDTO | undefined {
      for (const heading of list) {
        if (heading.id === id) {
          return heading
        }
        if (heading.subHeadings && heading.subHeadings.length > 0) {
          const found = findInList(heading.subHeadings)
          if (found) {
            return found
          }
        }
      }
      return undefined
    }
    return findInList(headings.value)
  }

  /**
   * Get the immediate parent of a heading by ID.
   * Returns null if the heading is top-level or not found.
   */
  function getParentHeading(id: number): HeadingDTO | null {
    function findParentInList(list: HeadingDTO[], parent: HeadingDTO | null): HeadingDTO | null {
      for (const heading of list) {
        if (heading.id === id) {
          return parent
        }
        if (heading.subHeadings && heading.subHeadings.length > 0) {
          const found = findParentInList(heading.subHeadings, heading)
          if (found !== undefined) {
            return found
          }
        }
      }
      return undefined as unknown as HeadingDTO | null
    }
    const result = findParentInList(headings.value, null)
    return result === undefined ? null : result
  }

  /**
   * Get all ancestors of a heading (from top-level to immediate parent).
   * Returns an empty array if the heading is top-level or not found.
   */
  function getHeadingAncestors(id: number): HeadingDTO[] {
    function findAncestors(list: HeadingDTO[], ancestors: HeadingDTO[]): HeadingDTO[] | null {
      for (const heading of list) {
        if (heading.id === id) {
          return ancestors
        }
        if (heading.subHeadings && heading.subHeadings.length > 0) {
          const found = findAncestors(heading.subHeadings, [...ancestors, heading])
          if (found) {
            return found
          }
        }
      }
      return null
    }
    return findAncestors(headings.value, []) ?? []
  }

  /**
   * Find a top-level heading by its abbreviation (case-insensitive)
   */
  function getHeadingByAbbreviation(abbr: string): HeadingDTO | undefined {
    const normalized = abbr.toUpperCase()
    return headings.value.find((h) => h.abbreviation.toUpperCase() === normalized)
  }

  /**
   * Find a subheading by its abbreviation within a parent heading (case-insensitive).
   * Only searches direct children, not nested subheadings.
   */
  function getSubheadingByAbbreviation(
    parentAbbr: string,
    subAbbr: string,
  ): HeadingDTO | undefined {
    const parent = getHeadingByAbbreviation(parentAbbr)
    if (!parent?.subHeadings) {
      return undefined
    }
    const normalizedSub = subAbbr.toUpperCase()
    return parent.subHeadings.find((s) => s.abbreviation.toUpperCase() === normalizedSub)
  }

  /**
   * Find a nested subheading by abbreviation path.
   * @param abbreviations Array of abbreviations from top-level to nested (e.g., ['SPZ', 'EN', 'A2'])
   * @returns The deepest heading matching the path, or undefined if not found
   */
  function getHeadingByAbbreviationPath(abbreviations: string[]): HeadingDTO | undefined {
    if (abbreviations.length === 0) {
      return undefined
    }

    const [firstAbbreviation, ...remainingAbbreviations] = abbreviations
    if (!firstAbbreviation) {
      return undefined
    }

    let current: HeadingDTO | undefined = getHeadingByAbbreviation(firstAbbreviation)
    if (!current) {
      return undefined
    }

    for (const abbreviation of remainingAbbreviations) {
      const normalized = abbreviation.toUpperCase()
      current = current.subHeadings?.find((s) => s.abbreviation.toUpperCase() === normalized)
      if (!current) {
        return undefined
      }
    }

    return current
  }

  const topLevelHeadings = computed(() => {
    return headings.value.filter((h) => h.parentId === null)
  })

  const allHeadingsFlat = computed(() => {
    const result: HeadingDTO[] = []
    function addHeadingsRecursively(list: HeadingDTO[]) {
      for (const heading of list) {
        result.push(heading)
        if (heading.subHeadings && heading.subHeadings.length > 0) {
          addHeadingsRecursively(heading.subHeadings)
        }
      }
    }
    addHeadingsRecursively(headings.value)
    return result
  })

  // ============================================
  // Return
  // ============================================

  return {
    // Shared state
    isLoading: computed(() => isLoading.value),
    isInitialized: computed(() => isInitialized.value),
    error: computed(() => error.value),

    // Core actions
    initializeMetadata,
    refreshMetadata,

    // Semester state
    selectedSemester: computed(() => selectedSemester.value),
    availableSemesters: computed(() => availableSemesters.value),

    // Semester actions
    selectSemester,
    clearSelectedSemester,
    getSemesterById,

    // Heading state
    headings: computed(() => headings.value),
    topLevelHeadings,
    allHeadingsFlat,

    // Heading actions
    getHeadingById,
    getParentHeading,
    getHeadingAncestors,
    getHeadingByAbbreviation,
    getSubheadingByAbbreviation,
    getHeadingByAbbreviationPath,
  }
}
